import { debounce } from 'lodash';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

const useClientSize = (
  elementRef: HTMLElement | null,
  options?: { observeHeight?: boolean; observeWidth?: boolean },
) => {
  const [clientWidth, setClientWidth] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const currentSize = useRef({ width: 0, height: 0 });
  const debouncedHandleResizeEnd = useMemo(
    () =>
      debounce(() => setIsResizing(false), 200, {
        leading: false,
        trailing: true,
      }),
    [],
  );

  const updateClientSize = useCallback(() => {
    if (
      elementRef &&
      !(
        (elementRef.clientWidth === currentSize.current.width &&
          options?.observeHeight === false) ||
        (elementRef.clientHeight === currentSize.current.height && options?.observeWidth === false)
      )
    ) {
      setIsResizing(true);
      debouncedHandleResizeEnd();
    }
    const height = elementRef?.clientHeight || 0;
    const width = elementRef?.clientWidth || 0;
    setClientWidth(width);
    setClientHeight(height);
    currentSize.current = {
      width,
      height,
    };
  }, [elementRef, debouncedHandleResizeEnd, options?.observeHeight, options?.observeWidth]);

  useIsomorphicLayoutEffect(() => {
    const height = elementRef?.clientHeight || 0;
    const width = elementRef?.clientWidth || 0;
    setClientWidth(width);
    setClientHeight(height);
    currentSize.current = {
      width,
      height,
    };
  }, [updateClientSize, elementRef]);

  useEffect(() => {
    if (!elementRef) {
      return () => {};
    }
    const observer = new ResizeObserver(() => {
      updateClientSize();
    });

    if (observer.observe) {
      observer.observe(elementRef);
      return () => observer.unobserve(elementRef);
    }
    return () => {};
  }, [elementRef, updateClientSize]);

  return { clientWidth, clientHeight, isResizing };
};

export default useClientSize;
